import { useList, useCustom } from "@refinedev/core";
import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { notify } from '@/lib/notify';
import {
  Activity,
  User,
  UserCog,
  Calendar,
  Filter,
  Search,
  Eye,
  X,
  Plus,
  Pencil,
  Trash2,
  LogIn,
  LogOut,
  Download,
  Upload,
  CheckCircle,
  XCircle,
  Send,
  Ban,
  Settings,
  Bot,
  ExternalLink,
  BarChart3,
  TrendingUp,
  Users,
  FileText,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DataTable, type Column, type SortState, type BulkAction } from "@/components/tables";
import { FormHeader } from "@/components/forms";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import type { AuditLog, AuditLogsFilters, FieldChange, ActorType } from "./types";

/**
 * Маппинг entityType из аудит-логов → маршрут в админке.
 * show — страница show (предпочтительно), edit — страница редактирования,
 * list — страница списка (если нет show/edit для конкретной записи).
 */
const ENTITY_ROUTE_MAP: Record<string, { show?: string; edit?: string; list?: string }> = {
  User: { show: '/users/show', edit: '/users/edit', list: '/users' },
  AdminUser: { show: '/admin-users/show', edit: '/admin-users/edit', list: '/admin-users' },
  Course: { show: '/courses/show', edit: '/courses/edit', list: '/courses' },
  SubscriptionPlan: { show: '/subscription-plans/show', edit: '/subscription-plans/edit', list: '/subscription-plans' },
  Subscription: { show: '/subscriptions/show', edit: '/subscriptions/edit', list: '/subscriptions' },
  Payment: { show: '/payments/show', list: '/payments' },
  Order: { show: '/orders/show', list: '/orders' },
  Banner: { edit: '/banners/edit', list: '/banners' },
  Teacher: { show: '/teachers/show', edit: '/teachers/edit', list: '/teachers' },
  TelegramChannel: { edit: '/telegram-channels/edit', list: '/telegram-channels' },
  ChannelMembership: { show: '/channel-memberships/show', list: '/channel-memberships' },
  KnowledgeDocument: { show: '/knowledge-documents/show', list: '/knowledge-documents' },
  Broadcast: { show: '/broadcasts/show', list: '/broadcasts' },
  Trigger: { edit: '/triggers/edit', list: '/triggers' },
  Role: { edit: '/roles/edit', list: '/roles' },
  SupportTicket: { show: '/support-tickets/show', list: '/support-tickets' },
  Language: { edit: '/languages/edit', list: '/languages' },
  ChannelPass: { show: '/channel-passes/show', edit: '/channel-passes/edit', list: '/channel-passes' },
};

/**
 * Получить URL для навигации к сущности
 */
function getEntityUrl(entityType: string, entityId: string): string | null {
  const routes = ENTITY_ROUTE_MAP[entityType];
  if (!routes || entityId === 'unknown') {return null;}

  if (routes.show) {return `${routes.show}/${entityId}`;}
  if (routes.edit) {return `${routes.edit}/${entityId}`;}
  return null;
}

/**
 * Получить URL для навигации к списку сущностей
 */
function getEntityListUrl(entityType: string): string | null {
  return ENTITY_ROUTE_MAP[entityType]?.list ?? null;
}

/**
 * Получить URL для навигации к актору
 */
function getActorUrl(log: AuditLog): string | null {
  if (log.actorType === 'ADMIN' && log.adminId) {
    return `/admin-users/show/${log.adminId}`;
  }
  if (log.actorType === 'USER' && log.userId) {
    return `/users/show/${log.userId}`;
  }
  return null;
}

/**
 * Кликабельная ссылка на сущность
 */
function EntityLink({ entityType, entityId, entityTypeLabel }: {
  entityType: string;
  entityId: string;
  entityTypeLabel: string;
}) {
  const url = getEntityUrl(entityType, entityId);
  const listUrl = getEntityListUrl(entityType);

  return (
    <div className="flex flex-col">
      {listUrl ? (
        <Link
          to={listUrl}
          className="font-medium text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {entityTypeLabel}
        </Link>
      ) : (
        <span className="font-medium">{entityTypeLabel}</span>
      )}
      {url ? (
        <Link
          to={url}
          className="text-xs text-blue-500 hover:underline flex items-center gap-0.5"
          onClick={(e) => e.stopPropagation()}
        >
          #{entityId}
          <ExternalLink className="h-3 w-3" />
        </Link>
      ) : (
        <span className="text-xs text-muted-foreground">#{entityId}</span>
      )}
    </div>
  );
}

/**
 * Кликабельная ссылка на актора
 */
function ActorLink({ log }: { log: AuditLog }) {
  const url = getActorUrl(log);
  const name = getActorName(log);
  const details = getActorDetails(log);

  return (
    <div className="flex items-center gap-2">
      {getActorIcon(log.actorType)}
      <div className="flex flex-col">
        {url ? (
          <Link
            to={url}
            className="font-medium text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {name}
          </Link>
        ) : (
          <span className="font-medium">{name}</span>
        )}
        {details && (
          <span className="text-xs text-muted-foreground">
            {details}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Иконка для действия
 */
function getActionIcon(action: string) {
  switch (action) {
    case "CREATE":
      return <Plus className="h-4 w-4" />;
    case "READ":
      return <Eye className="h-4 w-4" />;
    case "UPDATE":
      return <Pencil className="h-4 w-4" />;
    case "DELETE":
      return <Trash2 className="h-4 w-4" />;
    case "LOGIN":
      return <LogIn className="h-4 w-4" />;
    case "LOGOUT":
      return <LogOut className="h-4 w-4" />;
    case "EXPORT":
      return <Download className="h-4 w-4" />;
    case "IMPORT":
      return <Upload className="h-4 w-4" />;
    case "ACTIVATE":
      return <CheckCircle className="h-4 w-4" />;
    case "DEACTIVATE":
      return <Ban className="h-4 w-4" />;
    case "APPROVE":
      return <CheckCircle className="h-4 w-4" />;
    case "REJECT":
      return <XCircle className="h-4 w-4" />;
    case "SEND":
      return <Send className="h-4 w-4" />;
    case "CANCEL":
      return <Ban className="h-4 w-4" />;
    default:
      return <Settings className="h-4 w-4" />;
  }
}

/**
 * Цвет badge для действия
 */
function getActionVariant(action: string): "default" | "secondary" | "destructive" | "outline" {
  switch (action) {
    case "CREATE":
      return "default";
    case "UPDATE":
      return "secondary";
    case "DELETE":
      return "destructive";
    case "LOGIN":
    case "LOGOUT":
      return "outline";
    default:
      return "secondary";
  }
}

/**
 * Иконка для типа актора
 */
function getActorIcon(actorType: ActorType) {
  switch (actorType) {
    case "ADMIN":
      return <UserCog className="h-4 w-4 text-blue-500" />;
    case "USER":
      return <User className="h-4 w-4 text-green-500" />;
    case "SYSTEM":
      return <Bot className="h-4 w-4 text-purple-500" />;
    default:
      return <User className="h-4 w-4 text-muted-foreground" />;
  }
}

/**
 * Форматирование даты
 */
function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Получить имя актора
 */
function getActorName(log: AuditLog): string {
  if (log.actorType === "ADMIN") {
    return log.admin?.username || `Админ #${log.adminId}`;
  } else if (log.actorType === "USER") {
    if (log.user?.firstName || log.user?.lastName) {
      return [log.user.firstName, log.user.lastName].filter(Boolean).join(" ");
    } else if (log.user?.username) {
      return `@${log.user.username}`;
    }
    return `Пользователь #${log.userId}`;
  }
  return "Система";
}

/**
 * Получить детали актора
 */
function getActorDetails(log: AuditLog): string | null {
  if (log.actorType === "ADMIN" && log.admin?.email) {
    return log.admin.email;
  } else if (log.actorType === "USER" && log.user?.username) {
    return `@${log.user.username}`;
  }
  return null;
}

/**
 * Компонент отображения изменений
 */
function ChangesDisplay({ changes }: { changes: FieldChange[] }) {
  return (
    <div className="space-y-2">
      {changes.map((change) => (
        <div key={change.field} className="text-sm border-l-2 border-muted pl-3 py-1">
          <div className="font-medium text-muted-foreground mb-1">
            {change.fieldLabel}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-destructive line-through">
              {change.oldValue === null ? "(пусто)" : String(change.oldValue)}
            </span>
            <span className="text-muted-foreground">→</span>
            <span className="text-green-600">
              {change.newValue === null ? "(пусто)" : String(change.newValue)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Карточки статистики
 */
interface AuditStats {
  totalLogs: number;
  byAction: Record<string, number>;
  byEntityType: Record<string, number>;
  byActorType: Record<string, number>;
}

function StatsCards({ stats, isLoading }: { stats: AuditStats | undefined; isLoading: boolean }) {
  if (isLoading || !stats) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {['total', 'action', 'entity', 'actors'].map((key) => (
          <Card key={key}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-7 w-16 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const topAction = Object.entries(stats.byAction)
    .sort(([, a], [, b]) => b - a)[0];
  const topEntity = Object.entries(stats.byEntityType)
    .sort(([, a], [, b]) => b - a)[0];
  const adminCount = stats.byActorType['ADMIN'] ?? 0;
  const userCount = stats.byActorType['USER'] ?? 0;
  const systemCount = stats.byActorType['SYSTEM'] ?? 0;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Всего записей</CardTitle>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalLogs.toLocaleString('ru-RU')}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Частое действие</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {topAction ? (
            <>
              <div className="text-2xl font-bold">{topAction[1].toLocaleString('ru-RU')}</div>
              <p className="text-xs text-muted-foreground">{topAction[0]}</p>
            </>
          ) : (
            <div className="text-muted-foreground">—</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Частая сущность</CardTitle>
          <FileText className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {topEntity ? (
            <>
              <div className="text-2xl font-bold">{topEntity[1].toLocaleString('ru-RU')}</div>
              <p className="text-xs text-muted-foreground">{topEntity[0]}</p>
            </>
          ) : (
            <div className="text-muted-foreground">—</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">По акторам</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 text-sm">
            <div className="flex items-center gap-1">
              <UserCog className="h-3.5 w-3.5 text-blue-500" />
              <span className="font-medium">{adminCount}</span>
            </div>
            <div className="flex items-center gap-1">
              <User className="h-3.5 w-3.5 text-green-500" />
              <span className="font-medium">{userCount}</span>
            </div>
            <div className="flex items-center gap-1">
              <Bot className="h-3.5 w-3.5 text-purple-500" />
              <span className="font-medium">{systemCount}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Детали актора в Sheet
 */
function ActorDetailContent({ log, actorUrl }: { log: AuditLog; actorUrl: string | null }) {
  if (log.actorType === "ADMIN" && log.admin) {
    return (
      <>
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">Имя</span>
          {actorUrl ? (
            <Link to={actorUrl} className="font-medium text-primary hover:underline inline-flex items-center gap-1">
              {log.admin.username}
              <ExternalLink className="h-3 w-3" />
            </Link>
          ) : (
            <span className="font-medium">{log.admin.username}</span>
          )}
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Email</span>
          <span>{log.admin.email}</span>
        </div>
      </>
    );
  }

  if (log.actorType === "USER" && log.user) {
    return (
      <>
        {(log.user.firstName || log.user.lastName) && (
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Имя</span>
            {actorUrl ? (
              <Link to={actorUrl} className="font-medium text-primary hover:underline inline-flex items-center gap-1">
                {[log.user.firstName, log.user.lastName].filter(Boolean).join(" ")}
                <ExternalLink className="h-3 w-3" />
              </Link>
            ) : (
              <span className="font-medium">
                {[log.user.firstName, log.user.lastName].filter(Boolean).join(" ")}
              </span>
            )}
          </div>
        )}
        {log.user.username && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Username</span>
            <span>@{log.user.username}</span>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">ID</span>
          {actorUrl ? (
            <Link
              to={actorUrl}
              className="inline-flex items-center gap-1 text-sm text-blue-500 hover:underline bg-muted px-2 py-0.5 rounded"
            >
              {log.userId}
              <ExternalLink className="h-3 w-3" />
            </Link>
          ) : (
            <code className="text-sm bg-muted px-2 py-0.5 rounded">
              {log.userId}
            </code>
          )}
        </div>
      </>
    );
  }

  if (log.actorType === "SYSTEM") {
    return (
      <span className="text-muted-foreground">
        Автоматическое действие системы
      </span>
    );
  }

  return (
    <span className="text-muted-foreground">
      {log.actorType === "ADMIN" ? `Админ #${log.adminId}` : `Пользователь #${log.userId}`}
    </span>
  );
}

/**
 * Панель деталей лога
 */
function LogDetailSheet({
  log,
  open,
  onClose,
}: {
  log: AuditLog | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!log) {return null;}

  const entityUrl = getEntityUrl(log.entityType, log.entityId);
  const entityListUrl = getEntityListUrl(log.entityType);
  const actorUrl = getActorUrl(log);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Детали действия
          </SheetTitle>
          <SheetDescription>{log.description}</SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)]">
          <div className="space-y-6 pr-4">
            {/* Основная информация */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Информация</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Действие</span>
                  <Badge variant={getActionVariant(log.action)}>
                    {getActionIcon(log.action)}
                    <span className="ml-1">{log.actionLabel}</span>
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Сущность</span>
                  {entityListUrl ? (
                    <Link
                      to={entityListUrl}
                      className="font-medium text-primary hover:underline"
                    >
                      {log.entityTypeLabel}
                    </Link>
                  ) : (
                    <span className="font-medium">{log.entityTypeLabel}</span>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">ID сущности</span>
                  {entityUrl ? (
                    <Link
                      to={entityUrl}
                      className="inline-flex items-center gap-1 text-sm text-blue-500 hover:underline bg-muted px-2 py-0.5 rounded"
                    >
                      {log.entityId}
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  ) : (
                    <code className="text-sm bg-muted px-2 py-0.5 rounded">
                      {log.entityId}
                    </code>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Тип актора</span>
                  <Badge variant="outline" className="gap-1">
                    {getActorIcon(log.actorType)}
                    {log.actorTypeLabel}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Дата</span>
                  <span>{formatDate(log.createdAt)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Актор */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  {getActorIcon(log.actorType)}
                  {log.actorTypeLabel}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <ActorDetailContent log={log} actorUrl={actorUrl} />
              </CardContent>
            </Card>

            {/* Изменения */}
            {log.changes && log.changes.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    Изменённые поля ({log.changes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ChangesDisplay changes={log.changes} />
                </CardContent>
              </Card>
            )}

            {/* Технические данные */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">
                  Техническая информация
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <span className="text-muted-foreground text-sm">ID записи</span>
                  <code className="block text-xs bg-muted px-2 py-1 rounded mt-1 break-all">
                    {log.id}
                  </code>
                </div>
                {log.ipAddress && (
                  <div>
                    <span className="text-muted-foreground text-sm">IP адрес</span>
                    <code className="block text-xs bg-muted px-2 py-1 rounded mt-1">
                      {log.ipAddress}
                    </code>
                  </div>
                )}
                {log.userAgent && (
                  <div>
                    <span className="text-muted-foreground text-sm">User Agent</span>
                    <code className="block text-xs bg-muted px-2 py-1 rounded mt-1 break-all">
                      {log.userAgent}
                    </code>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Сырые данные */}
            {(log.oldData || log.newData) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    Данные (JSON)
                  </CardTitle>
                  <CardDescription>
                    Полные данные до и после изменения
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {log.oldData && (
                    <div>
                      <span className="text-muted-foreground text-sm">
                        До изменения
                      </span>
                      <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-auto max-h-40">
                        {JSON.stringify(log.oldData, null, 2)}
                      </pre>
                    </div>
                  )}
                  {log.newData && (
                    <div>
                      <span className="text-muted-foreground text-sm">
                        После изменения
                      </span>
                      <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-auto max-h-40">
                        {JSON.stringify(log.newData, null, 2)}
                      </pre>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Страница списка аудит-логов
 */
export function AuditLogsList() {
  useScrollRestoration();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL параметры для фильтрации по конкретному пользователю/админу
  const urlAdminId = searchParams.get("adminId");
  const urlUserId = searchParams.get("userId");

  const [page, setPage] = usePersistedState("audit-logs:page", 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>("audit-logs:sort", {
    field: "createdAt",
    order: "desc",
  });
  const [search, setSearch] = usePersistedState("audit-logs:search", "");
  const [actionFilter, setActionFilter] = usePersistedState<string>("audit-logs:action", "all");
  const [entityTypeFilter, setEntityTypeFilter] = usePersistedState<string>("audit-logs:entityType", "all");
  const [actorTypeFilter, setActorTypeFilter] = usePersistedState<string>("audit-logs:actorType", "all");
  const [dateFrom, setDateFrom] = usePersistedState("audit-logs:dateFrom", "");
  const [dateTo, setDateTo] = usePersistedState("audit-logs:dateTo", "");
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const pageSize = 20;

  // Устанавливаем тип актора на основе URL параметров.
  // При уходе на чистый /audit-logs (clearUrlFilter) сбрасываем сохранённый
  // фильтр к "all", иначе персистнутое значение из прошлой навигации залипает.
  useEffect(() => {
    if (urlAdminId) {
      setActorTypeFilter("ADMIN");
    } else if (urlUserId) {
      setActorTypeFilter("USER");
    } else {
      setActorTypeFilter("all");
    }
  }, [urlAdminId, urlUserId, setActorTypeFilter]);

  // Функция для очистки URL фильтра
  const clearUrlFilter = () => {
    navigate("/audit-logs", { replace: true });
  };

  // Загрузка фильтров
  const { result: filtersResult } = useCustom<AuditLogsFilters>({
    url: "/audit-logs/filters",
    method: "get",
  });

  const filters = filtersResult?.data;

  // Загрузка статистики
  const statsQueryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (dateFrom) {params.dateFrom = dateFrom;}
    if (dateTo) {params.dateTo = dateTo;}
    return params;
  }, [dateFrom, dateTo]);

  const { result: statsResult, query: statsQuery } = useCustom<AuditStats>({
    url: "/audit-logs/stats",
    method: "get",
    config: {
      query: statsQueryParams,
    },
  });

  const stats = statsResult?.data;

  // Подготовка фильтров для запроса
  const queryFilters = useMemo(() => {
    const result = [];
    if (actionFilter !== "all") {
      result.push({ field: "action", operator: "eq" as const, value: actionFilter });
    }
    if (entityTypeFilter !== "all") {
      result.push({ field: "entityType", operator: "eq" as const, value: entityTypeFilter });
    }
    if (actorTypeFilter !== "all") {
      result.push({ field: "actorType", operator: "eq" as const, value: actorTypeFilter });
    }
    if (search) {
      result.push({ field: "search", operator: "contains" as const, value: search });
    }
    if (dateFrom) {
      result.push({ field: "dateFrom", operator: "eq" as const, value: dateFrom });
    }
    if (dateTo) {
      result.push({ field: "dateTo", operator: "eq" as const, value: dateTo });
    }
    // Фильтрация по конкретному админу или пользователю из URL
    if (urlAdminId) {
      result.push({ field: "adminId", operator: "eq" as const, value: urlAdminId });
    }
    if (urlUserId) {
      result.push({ field: "userId", operator: "eq" as const, value: urlUserId });
    }
    return result;
  }, [actionFilter, entityTypeFilter, actorTypeFilter, search, dateFrom, dateTo, urlAdminId, urlUserId]);

  // Загрузка логов
  const { result: listData, query } = useList<AuditLog>({
    resource: "audit-logs",
    pagination: { currentPage: page, pageSize, mode: "server" },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listData?.data ?? [];
  const total = listData?.total ?? 0;
  const isLoading = query?.isLoading ?? false;

  // Подсчёт активных фильтров
  const activeFiltersCount = [
    actionFilter !== "all",
    entityTypeFilter !== "all",
    actorTypeFilter !== "all",
    dateFrom !== "",
    dateTo !== "",
  ].filter(Boolean).length;

  // Сброс фильтров
  const resetFilters = () => {
    setActionFilter("all");
    setEntityTypeFilter("all");
    setActorTypeFilter("all");
    setDateFrom("");
    setDateTo("");
    setSearch("");
    setPage(1);
  };

  // Bulk-действия
  const bulkActions: BulkAction<AuditLog>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          setSelectedLog(items[0]);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Экспорт',
      icon: <Download className="h-4 w-4" />,
      onClick: () => {
        notify.info('Экспорт аудит-логов — в разработке');
      },
      showOnlyWhenSelected: true,
    },
  ];

  // Колонки таблицы
  const columns: Column<AuditLog>[] = [
    {
      key: "createdAt",
      header: "Дата",
      width: "160px",
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDate(String(value))}
        </div>
      ),
    },
    {
      key: "action",
      header: "Действие",
      width: "140px",
      sortable: true,
      render: (_, log) => (
        <Badge variant={getActionVariant(log.action)} className="gap-1">
          {getActionIcon(log.action)}
          {log.actionLabel}
        </Badge>
      ),
    },
    {
      key: "entityType",
      header: "Сущность",
      width: "180px",
      sortable: true,
      render: (_, log) => (
        <EntityLink
          entityType={log.entityType}
          entityId={log.entityId}
          entityTypeLabel={log.entityTypeLabel}
        />
      ),
    },
    {
      key: "description",
      header: "Описание",
      render: (_, log) => (
        <div className="max-w-md truncate" title={log.description}>
          {log.description}
        </div>
      ),
    },
    {
      key: "actorType",
      header: "Кто",
      width: "200px",
      sortable: true,
      render: (_, log) => <ActorLink log={log} />,
    },
    {
      key: "changes",
      header: "Изменения",
      width: "100px",
      render: (_, log) =>
        log.changes && log.changes.length > 0 ? (
          <Badge variant="outline">{log.changes.length} полей</Badge>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Аудит-логи"
        description="История действий администраторов и пользователей в системе"
        sticky
      />

      {/* Статистика */}
      <StatsCards stats={stats} isLoading={statsQuery?.isLoading ?? false} />

      {/* Индикатор фильтрации по конкретному пользователю/админу */}
      {(urlAdminId || urlUserId) && (
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {urlAdminId ? (
                  <UserCog className="h-4 w-4 text-blue-500" />
                ) : (
                  <User className="h-4 w-4 text-green-500" />
                )}
                <span className="text-sm font-medium">
                  {urlAdminId
                    ? `Показаны логи администратора #${urlAdminId}`
                    : `Показаны логи пользователя #${urlUserId}`}
                </span>
                {/* Ссылка на профиль */}
                <Link
                  to={urlAdminId ? `/admin-users/show/${urlAdminId}` : `/users/show/${urlUserId}`}
                  className="text-sm text-blue-500 hover:underline inline-flex items-center gap-0.5"
                >
                  Профиль
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearUrlFilter}
                className="h-7 gap-1"
              >
                <X className="h-3.5 w-3.5" />
                Показать все
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Панель фильтров */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
            {/* Поиск */}
            <div className="lg:col-span-2">
              <Label htmlFor="search" className="sr-only">
                Поиск
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Поиск по ID, типу, имени..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Фильтр по действию */}
            <div>
              <Label htmlFor="action-filter" className="sr-only">
                Действие
              </Label>
              <Select
                value={actionFilter}
                onValueChange={(v) => {
                  setActionFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger id="action-filter">
                  <SelectValue placeholder="Действие" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все действия</SelectItem>
                  {filters?.actions?.map((action: { value: string; label: string }) => (
                    <SelectItem key={action.value} value={action.value}>
                      {action.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Фильтр по типу сущности */}
            <div>
              <Label htmlFor="entity-filter" className="sr-only">
                Сущность
              </Label>
              <Select
                value={entityTypeFilter}
                onValueChange={(v) => {
                  setEntityTypeFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger id="entity-filter">
                  <SelectValue placeholder="Сущность" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все сущности</SelectItem>
                  {filters?.entityTypes?.map((type: { value: string; label: string }) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Фильтр по типу актора */}
            <div>
              <Label htmlFor="actor-filter" className="sr-only">
                Кто
              </Label>
              <Select
                value={actorTypeFilter}
                onValueChange={(v) => {
                  setActorTypeFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger id="actor-filter">
                  <SelectValue placeholder="Кто" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  {filters?.actorTypes?.map((type: { value: string; label: string }) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Кнопка сброса */}
            <div className="flex items-center gap-2">
              {activeFiltersCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className="gap-1"
                >
                  <Filter className="h-4 w-4" />
                  Сбросить ({activeFiltersCount})
                </Button>
              )}
            </div>
          </div>

          {/* Фильтры по дате */}
          <Separator className="my-4" />
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">С даты</Label>
              <div className="w-48">
                <DatePicker
                  value={dateFrom ? new Date(dateFrom) : null}
                  onChange={(date) => {
                    setDateFrom(date ? format(date, 'yyyy-MM-dd') : '');
                    setPage(1);
                  }}
                  placeholder="Без ограничения"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">По дату</Label>
              <div className="w-48">
                <DatePicker
                  value={dateTo ? new Date(dateTo) : null}
                  onChange={(date) => {
                    setDateTo(date ? format(date, 'yyyy-MM-dd') : '');
                    setPage(1);
                  }}
                  placeholder="Без ограничения"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Таблица */}
      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(log) => setSelectedLog(log)}
        emptyMessage="Нет записей в журнале аудита"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      {/* Панель деталей */}
      <LogDetailSheet
        log={selectedLog}
        open={!!selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
}
